import React, { useState, useMemo, useCallback } from "react";
import {
  Layout as AntLayout,
  Menu,
  Typography,
  Button,
  Badge,
  Drawer,
  Grid,
  Avatar,
  Modal,
} from "antd";
import {
  SettingOutlined,
  MenuOutlined,
  LogoutOutlined,
  AimOutlined,
  DollarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../services/api";
import { QUERY_KEYS, REFETCH_INTERVALS } from "../../utils/constants";
import { ThemeToggle } from "../common/ThemeToggle";
import { useAuth } from "../../contexts/AuthContext";
import {
  ClipboardSlash,
  Crowd,
  DuplicatePlus,
  GridList,
  Sliders,
  UserList,
  ViewAll,
} from "../../icons";

const { Content, Sider } = AntLayout;
const { Title } = Typography;
const { useBreakpoint } = Grid;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { user, logout } = useAuth();

  // Get running executions count for badge
  const { data: dashboardData } = useQuery({
    queryKey: [QUERY_KEYS.DASHBOARD],
    queryFn: () => apiClient.getDashboardStats(),
    refetchInterval: REFETCH_INTERVALS.MEDIUM,
    select: (data) => data?.stats, // Only select what we need
  });

  const handleLogout = useCallback(() => {
    Modal.confirm({
      title: "Logout",
      content: "Are you sure you want to log out?",
      okText: "Yes, logout",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => {
        logout();
      },
    });
  }, [logout]);

  const runningExecutions = dashboardData?.running_executions || 0;

  // Memoize menu items to prevent unnecessary re-renders - filter by role
  const menuItems = useMemo(() => {
    const allItems = [
      {
        key: "/overview",
        icon: location.pathname === "/overview" ? <ViewAll /> : <ViewAll />,
        label: "Dashboard",
        roles: ["super_admin", "admin", "manager", "employee"],
      },
      {
        key: "/runs",
        icon:
          runningExecutions > 0 ? (
            <Badge count={runningExecutions} size="small" offset={[10, 0]}>
              {location.pathname === "/runs" ? <Sliders /> : <Sliders />}
            </Badge>
          ) : location.pathname === "/runs" ? (
            <Sliders />
          ) : (
            <Sliders />
          ),
        label: "Run Configurations",
        roles: ["super_admin", "admin", "manager"],
      },
      {
        key: "/properties",
        icon: location.pathname === "/properties" ? <GridList /> : <GridList />,
        label: "Properties",
        roles: ["super_admin", "admin", "manager"],
      },
      {
        key: "/my-properties",
        icon: <UserList />,
        label: "My Properties",
        roles: ["super_admin", "admin", "manager", "employee"],
      },
      {
        key: "/focus",
        icon: <AimOutlined />,
        label: "Focus",
        roles: ["super_admin", "admin", "manager", "employee"],
      },
      {
        key: "/sales",
        icon: <DollarOutlined />,
        label: "Sales Dashboard",
        roles: ["super_admin", "admin", "manager", "employee"],
      },
      {
        key: "/assign-properties",
        icon: <DuplicatePlus />,
        label: "Assign Properties",
        roles: ["super_admin", "admin", "manager"],
      },
      {
        key: "/blacklists",
        icon:
          location.pathname === "/blacklists" ? (
            <ClipboardSlash />
          ) : (
            <ClipboardSlash />
          ),
        label: "Blacklists",
        roles: ["super_admin", "admin", "manager"],
      },
      {
        key: "/employees",
        icon: <Crowd />,
        label: "Employees",
        roles: ["super_admin", "admin", "manager"],
      },
      {
        key: "/documents",
        icon: <FileTextOutlined />,
        label: "Documents",
        roles: ["super_admin", "admin", "manager"],
      },
    ];

    // Filter menu items based on user role
    return allItems
      .filter((item) => !item.roles || item.roles.includes(user?.role || ""))
      .map(({ roles: _roles, ...item }) => item);
  }, [runningExecutions, location.pathname, user?.role]);

  // Memoize click handlers
  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      navigate(key);
      if (!screens.lg) {
        setMobileMenuVisible(false);
      }
    },
    [navigate, screens.lg]
  );

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuVisible((prev) => !prev);
  }, []);

  // Mobile menu component
  const MobileMenu = () => (
    <Drawer
      title="Navigation"
      placement="left"
      onClose={() => setMobileMenuVisible(false)}
      open={mobileMenuVisible}
      bodyStyle={{ padding: 0 }}
      width={280}
    >
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid var(--border-color)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={4} style={{ margin: 0, color: "var(--primary-color)" }}>
          🏠 Blync
        </Title>
        <ThemeToggle variant="simple" size="small" />
      </div>
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ border: "none" }}
      />
    </Drawer>
  );

  // Modern Desktop Sidebar
  const DesktopSider = () => (
    <Sider
      width={280}
      style={{
        background: "#F8F9FA",
        height: "100vh",
        overflow: "auto",
        position: "sticky",
        top: 0,
        left: 0,
      }}
    >
      <div
        className="modern-sidebar"
        style={{
          background: "#F8F9FA",
          border: "none",
          padding: "20px 16px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* User Header Section */}
        <div className="sidebar-header" style={{ marginBottom: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <Avatar
              size={40}
              style={{
                backgroundColor: "#5B21B6",
                marginRight: "12px",
                flexShrink: 0,
              }}
            >
              {user
                ? `${user.full_name?.split(" ")[0]?.[0] || ""}${
                    user.full_name?.split(" ")[1]?.[0] || ""
                  }`.toUpperCase()
                : "U"}
            </Avatar>
            {user && (
              <div style={{ flex: 1 }}>
                <div
                  className="user-name"
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#1a1a1a",
                  }}
                >
                  {user.full_name}
                </div>
                <div
                  className="user-email"
                  style={{ fontSize: "12px", color: "#6B7280" }}
                >
                  {user.email}
                </div>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <ThemeToggle variant="simple" size="small" showLabel={false} />
            </div>
          </div>

          {/* Mobile hamburger menu button */}
          {!screens.lg && (
            <div style={{ marginBottom: "16px" }}>
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={toggleMobileMenu}
                style={{
                  fontSize: "20px",
                  width: 40,
                  height: 40,
                  color: "#6B7280",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                }}
                aria-label="Open navigation menu"
              />
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="sidebar-divider" />

        {/* Main Navigation */}
        <div
          className="main-navigation"
          style={{ flex: 1, marginBottom: "12px" }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: "none",
              background: "transparent",
            }}
            className="modern-sidebar-menu"
          />
        </div>

        {/* Bottom Section */}
        <div className="bottom-section">
          <Menu
            mode="inline"
            className="modern-sidebar-menu"
            style={{ border: "none", background: "transparent" }}
          >
            <Menu.Item
              key="settings"
              icon={<SettingOutlined />}
              onClick={() => navigate("/settings")}
            >
              Settings
            </Menu.Item>
            <Menu.Item key="logout" onClick={handleLogout}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <LogoutOutlined />
                <span>Logout</span>
              </div>
            </Menu.Item>
          </Menu>
        </div>
      </div>
    </Sider>
  );

  return (
    <AntLayout style={{ minHeight: "100vh", background: "var(--bg-color)" }}>
      {/* Content area with sidebar */}
      <AntLayout style={{ background: "var(--bg-color)" }}>
        {/* Desktop Sidebar */}
        {screens.lg && <DesktopSider />}

        {/* Mobile Menu */}
        {!screens.lg && <MobileMenu />}

        <Content
          style={{
            padding: screens.sm ? "24px" : "16px",
            minHeight: "100vh",
            background: "var(--bg-color)",
            overflow: "auto",
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default React.memo(Layout);
